import React, { useState } from 'react';
import { toast } from 'react-toastify';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import Dialog from '../../organisms/Dialog';
import { Operation, sendToValidation, removeValidation } from './OperationPageViewBackend';

interface ValidationConfigSectionProps {
    operation: Operation;
    onOperationChange: (op: Operation) => void;
    internalManagers: { value: string; label: string }[];
}

const VALIDATION_CHECKBOXES = [
    { key: 'validation_xml', label: 'XML' },
    { key: 'validation_receipt_stub', label: 'Canhoto' },
    { key: 'validation_contact', label: 'Ligação/Email' },
    { key: 'validation_sefaz', label: 'SEFAZ' },
    { key: 'validation_invoice_pdf', label: 'NF PDF' },
    { key: 'validation_accepted_bill', label: 'Duplicata c/ Aceite' },
    { key: 'validation_purchase_contract', label: 'Contrato Compra/Venda' },
] as const;

const ValidationConfigSection: React.FC<ValidationConfigSectionProps> = ({ operation, onOperationChange, internalManagers }) => {
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [justification, setJustification] = useState('');

    const handleSend = async () => {
        if (!operation.operation_type || !operation.obligation_type || !operation.trader?.trim() || !operation.analysis_criteria) {
            toast.error('Preencha todos os campos da configuração do lote.');
            return;
        }
        if (operation.analysis_criteria === 'PARCIAL' && (!operation.analysis_receivables_count || operation.analysis_receivables_count < 1)) {
            toast.error('Informe a quantidade de títulos para análise.');
            return;
        }
        const result = await sendToValidation(operation);
        if (result.success) {
            onOperationChange({ ...operation, sent_to_validation: result.sent_to_validation ?? true });
        }
    };

    const handleRemove = async () => {
        if (!justification.trim()) { toast.error('Informe a justificativa.'); return; }
        const success = await removeValidation(operation.credit_operations_id || '', justification.trim());
        if (success) {
            onOperationChange({
                ...operation, sent_to_validation: false, operation_type: '', obligation_type: '', trader: '',
                validation_xml: false, validation_receipt_stub: false, validation_contact: false, validation_sefaz: false,
                validation_invoice_pdf: false, validation_accepted_bill: false, validation_purchase_contract: false,
                analysis_criteria: '', analysis_receivables_count: 0,
            });
            setShowRemoveDialog(false);
            setJustification('');
        }
    };

    const update = (fields: Partial<Operation>) => onOperationChange({ ...operation, ...fields });
    const disabled = operation.sent_to_validation;

    return (
        <>
            <div className="mb-6 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Configuração do Lote para Validação</h3>
                        {disabled && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                ✓ Já enviado para validação
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={handleSend} disabled={disabled}
                            className="bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-500 dark:hover:bg-gray-600">
                            Enviar para Validação
                        </button>
                        {disabled && (
                            <button type="button" onClick={() => setShowRemoveDialog(true)}
                                className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded dark:bg-red-500 dark:hover:bg-red-600">
                                Remover da Validação
                            </button>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className="col-span-3">
                        <SelectTextField id="operation_type" label="Tipo de Operação" value={operation.operation_type || ''}
                            onChange={(e) => update({ operation_type: e.target.value })} disabled={disabled}
                            items={[{ value: 'CPRF', label: 'CPRF' }, { value: 'CESSAO', label: 'Cessão' }]} />
                    </div>
                    <div className="col-span-3">
                        <SelectTextField id="obligation_type" label="Obrigação" value={operation.obligation_type || ''}
                            onChange={(e) => update({ obligation_type: e.target.value })} disabled={disabled}
                            items={[{ value: 'SEM_OBRIGACAO', label: 'Sem Obrigação' }, { value: 'COM_REGRESSO', label: 'Com Regresso' }, { value: 'SEM_REGRESSO', label: 'Sem Regresso' }]} />
                    </div>
                    <div className="col-span-3">
                        <SelectTextField id="trader" label="Trader" value={operation.trader || ''}
                            onChange={(e) => update({ trader: e.target.value })} disabled={disabled} items={internalManagers} />
                    </div>
                    <div className="col-span-3">
                        <SelectTextField id="analysis_criteria" label="Critério de Análise" value={operation.analysis_criteria || ''}
                            onChange={(e) => update({ analysis_criteria: e.target.value })} disabled={disabled}
                            items={[{ value: 'TOTAL', label: 'Total' }, { value: 'PARCIAL', label: 'Parcial' }]} />
                    </div>
                    {operation.analysis_criteria === 'PARCIAL' && (
                        <div className="col-span-3">
                            <TextEdit label={`Qtd. Títulos (max: ${operation.total_receivables})`}
                                value={(operation.analysis_receivables_count || 0).toString()}
                                onChange={(e) => { const val = parseInt(e.target.value) || 0; update({ analysis_receivables_count: Math.min(Math.max(val, 0), operation.total_receivables) }); }}
                                disabled={disabled} />
                        </div>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Validações</label>
                    <div className="grid grid-cols-2 gap-2">
                        {VALIDATION_CHECKBOXES.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                <input type="checkbox" checked={(operation as any)[key] || false}
                                    onChange={(e) => update({ [key]: e.target.checked })} disabled={disabled}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                {label}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <Dialog id="remove-validation-confirmation" show={showRemoveDialog}
                onClose={() => { setShowRemoveDialog(false); setJustification(''); }}
                header="Remover da Validação"
                body={
                    <div className="p-6 space-y-4">
                        <p className="text-gray-700 dark:text-gray-300">Tem certeza que deseja remover esta operação da validação?</p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Justificativa *</label>
                            <textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={3}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder="Informe o motivo da remoção..." />
                        </div>
                    </div>
                }
                footer={
                    <div className="flex space-x-2">
                        <button type="button" onClick={() => { setShowRemoveDialog(false); setJustification(''); }}
                            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                        <button type="button" onClick={handleRemove}
                            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded">Confirmar Remoção</button>
                    </div>
                }
            />
        </>
    );
};

export default ValidationConfigSection;
